/**
 * Created by olga on 23.10.2024
 */

const Meta = function () { 
    cleverapps.EventEmitter.call(this);

    this.mainLocationId = 0;
    this.extra = {};
    this.locations = {};

    Object.values(Meta.GetAvailableLocations()).forEach((locationConfig) => {
        this.locations[locationConfig.id] = this.createLocationMeta(locationConfig);
    });

    LocationsClientValidator.validate();
};

Meta.prototype = Object.create(cleverapps.EventEmitter.prototype);
Meta.prototype.constructor = Meta;

Meta.prototype.load = function () {
    const data = cleverapps.dataLoader.load(DataLoader.TYPES.META);
    this.updateData(data);

    this.switchLocation(this.getMainLocationId());
};

Meta.prototype.save = function (fromSever) {
    if (cleverapps.backwardCompatibility.has(DataLoader.TYPES.META)) {
        cleverapps.backwardCompatibility.save(DataLoader.TYPES.META, fromSever);
    } else {
        cleverapps.dataLoader.save(DataLoader.TYPES.META, this.getData());
        if (!fromSever) {
            connector.synchronizer.addSendTask("metha");
        }
    }
};

Meta.prototype.updateInfo = function (serverData) {
    this.mainLocationId = 0;

    Object.values(this.locations).forEach((locationMeta) => {
        locationMeta.reset();
    });

    this.updateData(serverData);

    this.save(true);

    if (cleverapps.highscore) {
        cleverapps.highscore.updateInfo(serverData);
        cleverapps.highscore.save(true);
    }
};

Meta.prototype.getInfo = function () {
    let data;

    if (cleverapps.backwardCompatibility.has(DataLoader.TYPES.META)) {
        data = cleverapps.backwardCompatibility.getInfo(DataLoader.TYPES.META);
    } else {
        data = this.getData();
    }

    if (cleverapps.highscore) {
        Object.assign(data, cleverapps.highscore.getInfo());
    }

    return data;
};

Meta.prototype.updateData = function (data) {
    data = data || {};

    this.mainLocationId = data.main || 0;
    this.extra = data.extra || {};

    const retiredLocationIds = LocationConfigResolver.listRetiredLocationIds(cleverapps.config.locations);

    Object.keys(data.locations || {}).forEach((locationId) => {
        if (Meta.LEGACY_LOCATIONS.includes(locationId) || retiredLocationIds.includes(locationId)) {
            delete this.locations[locationId];
            return;
        }

        let locationMeta = this.locations[locationId];
        if (!locationMeta) {
            locationMeta = this.locations[locationId] = this.createLocationMeta({
                id: locationId,
                unknown: true
            });
        }

        locationMeta.updateInfo(data.locations[locationId]);
    });

    if (cleverapps.knockoutGame) {
        cleverapps.knockoutGame.updateData(data);
    }

    if (["mergecraft", "wondermerge", "fairy", "hustlemerge"].includes(cleverapps.config.name)) {
        // restore collections for old users
        const locationMeta = this.getLocationMeta("collections");
        if (!locationMeta.isUnknown() && !locationMeta.isStarted() && !locationMeta.isFinished() && locationMeta.isPermanent() && locationMeta.canStart()) {
            locationMeta.start(Date.now());
            this.needSaveAfterMigration = true;
        }
    }
};

Meta.prototype.getData = function () {
    const locations = {};

    Object.values(this.locations).forEach((locationMeta) => {
        if (locationMeta.locationConfig.isMain) {
            return;
        }

        const info = locationMeta.getInfo();

        if (cleverapps.isEmpty(info)) {
            return;
        }

        locations[locationMeta.locationId] = info;
    });

    const data = {
        main: this.mainLocationId || undefined
    };

    if (!cleverapps.isEmpty(locations)) {
        data.locations = locations;
    }

    if (!cleverapps.isEmpty(this.extra)) {
        data.extra = this.extra;
    }

    if (cleverapps.knockoutGame) {
        Object.assign(data, cleverapps.knockoutGame.getData());
    }

    return data;
};

Meta.prototype.getLocationMeta = function (locationId) {
    return this.locations[locationId] || this.createLocationMeta({
        id: locationId,
        unknown: true
    });
};

Meta.prototype.createLocationMeta = function (locationConfig) {
    locationConfig.locationId = locationConfig.id;
    locationConfig.locationType = locationConfig.locationType !== undefined ? locationConfig.locationType : "unknown";
    locationConfig.slotId = locationConfig.slotId !== undefined ? locationConfig.slotId : "unknown";
    locationConfig.slot = cleverapps.dataSlots.getSlot(locationConfig.slotId);

    locationConfig.isMain = cleverapps.isNumber(locationConfig.id) || locationConfig.id === "main";
    locationConfig.locationBundleId = locationConfig.unknown ? "location_unknown" : `location_${locationConfig.id}`;
    locationConfig.withOwnLives = locationConfig.usesEnergy && !locationConfig.isMain;
    locationConfig.withOwnGameLevel = locationConfig.gameLevel && !locationConfig.isMain;

    return new LocationMeta(locationConfig);
};

Meta.prototype.setExtra = function (key, value) {
    this.extra[key] = value;
};

Meta.prototype.getExtra = function (key) {
    return this.extra[key];
};

Meta.prototype.stopLocation = function () {
    if (typeof Map2dLocation !== "undefined" && Map2dLocation.currentLocation) {
        Map2dLocation.currentLocation.stop();
        Map2dLocation.currentLocation = undefined;
    }

    if (typeof Homefix !== "undefined" && Homefix.currentHomefix) {
        Homefix.currentHomefix.stop();
        Homefix.currentHomefix = undefined;
    }

    if (typeof Game !== "undefined" && Game.currentGame) {
        Game.currentGame.stop();
        Game.currentGame = undefined;
    }

    if (typeof Merge2 !== "undefined" && Merge2.current) {
        Merge2.current.stop();
        Merge2.current = undefined;
    }
};

Meta.prototype.switchLocation = function (locationId) {
    this.stopLocation();

    const locationMeta = cleverapps.meta.getLocationMeta(locationId);
    const locationConfig = locationMeta.locationConfig;
    const LocationClass = this.getLocationClass(locationId);

    const slot = cleverapps.config.editorMode || cleverapps.config.adminMode
        ? cleverapps.dataSlots.getSlot("fake")
        : locationConfig.slot;

    this.location = new LocationClass(locationConfig, slot);

    const bundle = bundles[locationConfig.locationBundleId];
    if (bundle && bundle.meta.debug && !cleverapps.config.adminMode) {
        cleverapps.throwAsync(`Switch to debug location ${this.location.locationId}`);
    }

    if (typeof LivesHelper !== "undefined") {
        LivesHelper.Switch(this.location.locationId);
    }

    if (typeof ExpHelper !== "undefined") {
        ExpHelper.Switch(this.location.locationId);
    }

    if (cleverapps.config.features.includes("game_level")) {
        GameLevelHelper.Switch(this.location.locationId);
    }

    if (typeof UnitsLibraryHelper !== "undefined") {
        UnitsLibraryHelper.Switch(this.location.locationId);
    }

    if (cleverapps.forces) {
        cleverapps.forces.loadLocationForces(this.location.locationId);
    }

    cleverapps.environment.setLocationId(locationConfig.isMain ? "main" : this.location.locationId);

    this.trigger("locationSwitched");

    cleverapps.playSession.logEventOnce(cleverapps.EVENTS.META_LOCATION);
};

Meta.prototype.gotoMainLocation = function (f) {
    if (cleverapps.config.demoMode) {
        cleverapps.unitsDemoMode.gotoUnitsDemoScene(f);
        return;
    }

    const locationId = this.getMainLocationId();
    
    if (this.selectedLocationId() !== locationId) {
        this.switchLocation(locationId);
    }

    this.gotoLocationScene(f);
};

Meta.prototype.gotoLocationScene = function (f) {
    const MainScene = this.getMainScene(this.selectedLocationId());
    cleverapps.scenes.replaceScene(typeof MainScene === "function" ? new MainScene() : MainScene, f);
};

Meta.prototype.getMainScene = function (locationId) {
    if (locationId === undefined) {
        locationId = this.getMainLocationId();
    }

    const LocationClass = this.getLocationClass(locationId);
    return LocationClass.GetMainScene();
};

Meta.prototype.isLocationAvailable = function (locationId) {
    const locationMeta = this.locations[locationId];
    if (!locationMeta || locationMeta.isUnknown()) {
        return false;
    }

    return true;
};

Meta.prototype.listAvailableLocations = function () {
    return Object.keys(this.locations)
        .filter((locationId) => this.isLocationAvailable(locationId));
};

Meta.prototype.isLocationActive = function (locationId) {
    const locationMeta = this.locations[locationId];
    return Boolean(locationMeta && locationMeta.isActive());
};

Meta.prototype.listActiveLocations = function () {
    return Object.keys(this.locations)
        .filter((locationId) => this.isLocationActive(locationId));
};

Meta.prototype.listPendingLocations = function () {
    const usedSlots = new Set();
    usedSlots.add(Meta.SLOT_MAIN);

    Object.values(this.locations).forEach((locationMeta) => {
        if (locationMeta.isStarted()) {
            usedSlots.add(locationMeta.slotId);
        }
    });

    return Object.values(this.locations)
        .filter((locationMeta) => !usedSlots.has(locationMeta.slotId) && locationMeta.canStart())
        .map((locationMeta) => locationMeta.locationId);
};

Meta.prototype.getMainLocationId = function () {
    return Meta.ToCompatLocationId(this.mainLocationId);
};

Meta.prototype.getSelectedLocation = function () {
    return this.location;
};

Meta.prototype.selectedLocationId = function () {
    return this.location.locationId;
};

Meta.prototype.getLocationClass = function (locationId) {
    locationId = Meta.ToCompatLocationId(locationId);

    const locationMeta = this.getLocationMeta(locationId);

    switch (locationMeta.locationType) {
        case "simple": return SimpleLocation;
        case "hose": return HoseLocation;
        case "shortmeta": return ShortMetaLocation;
        case "farm": return FarmLocation;
        case "homefix": return HomefixLocation;
        case "repair": return RepairLocation;
        case "merge": return MergeLocation;
    }

    return UnknownLocation;
};

Meta.prototype.startLocation = function (locationId) {
    const locationMeta = this.getLocationMeta(locationId);
    const locationConfig = locationMeta.locationConfig;

    if (locationMeta.isUnknown()) {
        cleverapps.throwAsync(`try to start unknown location ${locationId}`);
        return;
    }

    if (locationMeta.isStarted()) {
        cleverapps.throwAsync(`try to start started location ${locationId}`);
        return;
    }

    if (locationConfig.isMain) {
        this.mainLocationId = locationMeta.locationId;
    }

    locationMeta.start(Date.now());

    this.save();

    cleverapps.missionManager.clearLocationMissions(locationId);
    cleverapps.offerManager.clearLocationOffers(locationId);

    const slot = locationConfig.slot;
    slot.reset();

    const LocationClass = this.getLocationClass(locationMeta.locationId);
    if (LocationClass.OnLocationStart) {
        LocationClass.OnLocationStart(locationMeta);
    }

    cleverapps.metrics.logEvent(cleverapps.EVENTS.LOCATION.STARTED, {
        location: `${locationMeta.locationId}_${locationMeta.version}`
    });

    connector.conversions.trackEvent(cleverapps.EVENTS.CONVERSIONS.LOCATION_START);

    if ([1, 2, 3, 4, 5, 6, 7, 8, 10, 13, 15, 20, 22, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100].includes(locationMeta.locationId)) {
        connector.conversions.trackEvent(cleverapps.EVENTS.CONVERSIONS.LOCATION_REACH + locationMeta.locationId);
    }
};

Meta.prototype.finishLocation = function (locationId) {
    const locationMeta = this.getLocationMeta(locationId);

    if (locationMeta.isUnknown() && !locationMeta.canRemoveSilently()) {
        cleverapps.throwAsync(`try to finish unknown location ${locationId}`);
        return;
    }

    if (locationMeta.isFinished()) {
        cleverapps.throwAsync(`try to finish finished location ${locationId}`);
        return;
    }

    locationMeta.finish(Date.now());

    this.save();

    cleverapps.missionManager.clearLocationMissions(locationId);
    cleverapps.offerManager.clearLocationOffers(locationId);

    const LocationClass = this.getLocationClass(locationId);
    if (LocationClass.OnLocationFinish) {
        LocationClass.OnLocationFinish(locationMeta);
    }

    cleverapps.metrics.logEvent(cleverapps.EVENTS.LOCATION.FINISHED, {
        location: `${locationMeta.locationId}_${locationMeta.version}`
    });
};

Meta.prototype.removeSilents = function () {
    Object.values(this.locations).forEach((locationMeta) => {
        if (locationMeta.canRemoveSilently()) {
            this.finishLocation(locationMeta.locationId);
        }
    });
};

Meta.prototype.resetProgress = function () {
    this.location = undefined;

    this.mainLocationId = 0;
    Object.values(this.locations).forEach((locationMeta) => {
        locationMeta.reset();
    });

    this.switchLocation(this.getMainLocationId());
};

Meta.prototype.replaceOldLocation = function () {
    const locationMeta = Object.values(this.locations).find((locationMeta) => locationMeta.isOld() && locationMeta.isStarted() && !locationMeta.isFinished());
    if (!locationMeta) {
        return;
    }

    if (!locationMeta.canStart()) {
        this.finishLocation(locationMeta.locationId);
        cleverapps.metrics.logEvent(cleverapps.EVENTS.LOCATION.FINISHED_OLD, {
            location: locationMeta.locationId
        });
        return;
    }

    cleverapps.focusManager.display({
        focus: `replaceOldLocation${locationMeta.locationId}`,
        actions: [
            (f) => {
                new ReplaceOldMissionWindow(locationMeta.locationId);
                cleverapps.focusManager.onceNoWindowsListener = f;
            },

            (f) => {
                this.finishLocation(locationMeta.locationId);
                this.startLocation(locationMeta.locationId);
                cleverapps.metrics.logEvent(cleverapps.EVENTS.LOCATION.REPLACED_OLD, {
                    location: locationMeta.locationId
                });
                f();
            }
        ]
    });
};

// eslint-disable-next-line no-unused-vars
Meta.GetAvailableLocations = function () {
    return {};
};

Meta.ToCompatLocationId = function (locationId) {
    if (locationId === 0 && ["mergecraft", "wondermerge", "fairy", "hustlemerge", "map2deditor"].indexOf(cleverapps.config.name) !== -1) {
        locationId = "main";
    }

    return locationId;
};

Meta.SLOT_MAIN = "";
Meta.SLOTS = [Meta.SLOT_MAIN];

Meta.LEGACY_LOCATIONS = [];

if (cleverapps.config.type === "merge" || cleverapps.config.type === "merge2") {
    Meta.EXPEDITION_SLOT1 = "1";
    Meta.EXPEDITION_SLOT2 = "2";
    Meta.EXPEDITION_SLOT3 = "3";
    Meta.EXPEDITION_SLOT4 = "4";
    Meta.EXPEDITION_SLOT5 = "5";

    Meta.SLOTS = [Meta.SLOT_MAIN, Meta.EXPEDITION_SLOT1, Meta.EXPEDITION_SLOT2, Meta.EXPEDITION_SLOT3, Meta.EXPEDITION_SLOT4, Meta.EXPEDITION_SLOT5];
}

if (["riddles"].includes(cleverapps.config.name)) {
    Meta.EXPEDITION_SLOT1 = "1";
    Meta.EXPEDITION_SLOT2 = "2";
    Meta.SLOTS = [Meta.SLOT_MAIN, Meta.EXPEDITION_SLOT1, Meta.EXPEDITION_SLOT2];
}
